import "./Studio.css";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Studio = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    studioName: "",
    studioAddress: "",
    studioPhone: "",
    gstNumber: "",
    categories: "",
    priceRange: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);

  // Add body class on mount, remove on unmount
  useEffect(() => {
    document.body.classList.add('studio-active');
    return () => {
      document.body.classList.remove('studio-active');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Studio name validation
    if (!formData.studioName.trim()) {
      newErrors.studioName = "Studio name is required";
    } else if (formData.studioName.trim().length < 3) {
      newErrors.studioName = "Studio name must be at least 3 characters long";
    }

    // Studio address validation
    if (!formData.studioAddress.trim()) {
      newErrors.studioAddress = "Studio address is required";
    } else if (formData.studioAddress.trim().length < 10) {
      newErrors.studioAddress = "Please enter a complete address";
    }

    // Phone validation
    if (!formData.studioPhone) {
      newErrors.studioPhone = "Studio phone number is required";
    } else if (!/^\d{10}$/.test(formData.studioPhone.replace(/\D/g, ''))) {
      newErrors.studioPhone = "Please enter a valid 10-digit phone number";
    }

    // GST validation (optional but if provided, should be valid)
    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
      newErrors.gstNumber = "Please enter a valid GST number (15 characters)";
    }

    // Categories validation
    if (!formData.categories) {
      newErrors.categories = "Please select at least one category";
    }

    // Price range validation
    if (!formData.priceRange) {
      newErrors.priceRange = "Please select a price range";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Store form data in localStorage for demo purposes
      localStorage.setItem('studioDetails', JSON.stringify(formData));
      localStorage.setItem('signupStep', '2');
      
      console.log('Studio details saved:', formData);
      
      // Navigate to next step
      navigate("/Bank");
      
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Error submitting form. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, studioPhone: formatted });
    
    if (errors.studioPhone) {
      setErrors(prev => ({ ...prev, studioPhone: '' }));
    }
  };

  const formatGSTNumber = (value) => {
    // Remove all non-alphanumeric characters and convert to uppercase
    return value.replace(/[^A-Z0-9]/g, '').toUpperCase();
  };

  const handleGSTChange = (e) => {
    const formatted = formatGSTNumber(e.target.value);
    if (formatted.length <= 15) {
      setFormData({ ...formData, gstNumber: formatted });
      
      if (errors.gstNumber) {
        setErrors(prev => ({ ...prev, gstNumber: '' }));
      }
    }
  };

  const categories = [
    "Wedding Photography",
    "Portrait Photography",
    "Event Photography",
    "Commercial Photography",
    "Fashion Photography",
    "Product Photography",
    "Real Estate Photography",
    "Food Photography"
  ];

  const priceRanges = [
    "₹10,000 - ₹25,000",
    "₹25,000 - ₹50,000",
    "₹50,000 - ₹1,00,000",
    "₹1,00,000 - ₹2,00,000",
    "₹2,00,000+"
  ];

  return (
    <div className="studio-page-body">
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      
      <div className="studio-container">
        <button 
          className="studio-back-button" 
          onClick={() => navigate("/Personal")}
        >
          <i className="fas fa-arrow-left"></i> Back to Personal
        </button>
        
       

        <div className="studio-left">
          <img
            alt="Professional illustration of a photographer in studio with camera equipment"
            src="/images/studio.png"
          />
        </div>
        
        <div className="studio-right">
          <a className="studio-back-link" href="#" onClick={(e) => {
            e.preventDefault(); 
            navigate("/Personal");
          }}>
            <i className="fas fa-arrow-left"></i> Back
          </a>
          
          <h2 className="studio-title">Step 2 of 3 - Studio Details</h2>

          {/* Progress Indicator */}
          <div className="studio-progress">
            <div className="studio-progress-dot completed"></div>
            <div className="studio-progress-dot active"></div>
            <div className="studio-progress-dot"></div>
          </div>

          {errors.submit && (
            <div style={{ 
              color: "#e53935", 
              background: "rgba(229, 57, 53, 0.1)", 
              padding: "0.75rem", 
              borderRadius: "8px", 
              marginBottom: "1rem",
              fontSize: "0.875rem",
              fontWeight: "500"
            }}>
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="studio-form-group">
              <label htmlFor="studio-name">Studio Name *</label>
              <input 
                id="studio-name" 
                name="studioName"
                className="studio-input"
                placeholder="Enter your studio name" 
                type="text" 
                value={formData.studioName}
                onChange={handleChange}
                required
              />
              {errors.studioName && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.studioName}
                </span>
              )}
            </div>
            
            <div className="studio-form-group">
              <label htmlFor="studio-address">Studio Address *</label>
              <input 
                id="studio-address" 
                name="studioAddress"
                className="studio-input"
                placeholder="Enter your complete studio address" 
                type="text" 
                value={formData.studioAddress}
                onChange={handleChange}
                required
              />
              {errors.studioAddress && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.studioAddress}
                </span>
              )}
            </div>
            
            <div className="studio-form-group">
              <label htmlFor="studio-phone">Studio Phone Number *</label>
              <input 
                id="studio-phone" 
                name="studioPhone"
                className="studio-input"
                placeholder="(123) 456-7890" 
                type="text" 
                value={formData.studioPhone}
                onChange={handlePhoneChange}
                maxLength="14"
                required
              />
              {errors.studioPhone && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.studioPhone}
                </span>
              )}
            </div>
            
            <div className="studio-form-group">
              <label htmlFor="gst-number">GST Number (Optional)</label>
              <input 
                id="gst-number" 
                name="gstNumber"
                className="studio-input"
                placeholder="22AAAAA0000A1Z5 (15 characters)" 
                type="text" 
                value={formData.gstNumber}
                onChange={handleGSTChange}
                maxLength="15"
              />
              {errors.gstNumber && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.gstNumber}
                </span>
              )}
            </div>
            
            <div className="studio-form-group-divider"></div>
            
            <h3 style={{ 
              color: '#333', 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              Step 3 - Services & Pricing
            </h3>
            
            <div className="studio-form-group">
              <label htmlFor="categories">Serviceable Categories *</label>
              <select 
                id="categories" 
                name="categories"
                className="studio-select"
                value={formData.categories}
                onChange={handleChange}
                required
              >
                <option value="">Select Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>{category}</option>
                ))}
              </select>
              {errors.categories && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.categories}
                </span>
              )}
            </div>
            
            <div className="studio-form-group">
              <label htmlFor="price-range">Price Range *</label>
              <select 
                id="price-range" 
                name="priceRange"
                className="studio-select"
                value={formData.priceRange}
                onChange={handleChange}
                required
              >
                <option value="">Select Price Range</option>
                {priceRanges.map((range, index) => (
                  <option key={index} value={range}>{range}</option>
                ))}
              </select>
              {errors.priceRange && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.priceRange}
                </span>
              )}
            </div>
            
            <button 
              className="studio-btn" 
              type="submit" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                  Processing...
                </>
              ) : (
                <>
                  Next Step
                  <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i>
                </>
              )}
            </button>
          </form>

          <div className="studio-terms">
            By continuing, you agree to our 
            <a href="#" onClick={(e) => {
              e.preventDefault();
              console.log('Terms of Service clicked');
            }}>
              Terms of Service
            </a> and 
            <a href="#" onClick={(e) => {
              e.preventDefault();
              console.log('Privacy Policy clicked');
            }}>
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Studio;
